<!-- CouponUserForm.vue -->
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

// Store
import { useCouponUserStore } from "@/stores/useCouponUserStore";
import { useUserStore } from "@/stores/useUserStore";

// Modais
import AddCouponUserForm from "./AddCouponUserForm.vue";
import EditCouponUserForm from "./EditCouponUserForm.vue";
import DeleteCouponUserForm from "./DeleteCouponUserForm.vue";
import BatchDeleteCouponUserForm from "./BatchDeleteCouponUserForm.vue";
import CouponUserCardModal from "./CouponUserCardModal.vue";

import CouponUserHelpModal from "./CouponUserHelpModal.vue";

// Painel de filtros
import SearchCouponUserInput from "./SearchCouponUserInput.vue";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

const couponUserStore = useCouponUserStore();
const userStore = useUserStore();

const showHelpModal = ref(false);

const isSys = computed(() => userStore.currentUser?.userName === "Sys");

const callerId = computed(() =>
  userStore.currentUser?.userId != null
    ? String(userStore.currentUser.userId)
    : null
);

// ─────────────────────────────────────────────
// Estados dos modais
// ─────────────────────────────────────────────
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);
const selectedCouponUser = ref(null);

// Card modal
const showCardModal = ref(false);
const selectedCardCouponUserId = ref(null);

function handleOpenCard(cu) {
  selectedCardCouponUserId.value = cu.couponUserId;
  showCardModal.value = true;
}

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ padrão TagForm.vue
const isLoadingTable = ref(false);

// Dropdowns
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ─────────────────────────────────────────────
// TOAST
// ─────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ─────────────────────────────────────────────
// STATUS DO VÍNCULO
// ─────────────────────────────────────────────
function getRevokedLabel(revokedAt) {
  return revokedAt ? "Revogado" : "Ativo";
}
function getRevokedClass(revokedAt) {
  return revokedAt ? "status-tag status-red" : "status-tag status-green";
}

function getRedeemedLabel(redeemedAt) {
  return redeemedAt ? "Resgatado" : "Não usado";
}
function getRedeemedClass(redeemedAt) {
  return redeemedAt ? "status-tag status-green" : "status-tag status-yellow";
}

// ─────────────────────────────────────────────
// PERMISSÕES
// ─────────────────────────────────────────────
function canEdit() {
  return true;
}
function canDelete() {
  return true;
}

const anyEditableOnPage = computed(() =>
  couponUserStore.filteredCouponUsers.some(() => canEdit())
);

const isAllSelectedOnPage = computed({
  get: () => {
    const editableIds = couponUserStore.filteredCouponUsers
      .filter(() => canEdit())
      .map((cu) => cu.couponUserId);
    if (editableIds.length === 0) return false;
    return editableIds.every((id) =>
      couponUserStore.selectedCouponUserIds.includes(id)
    );
  },
  set: (value) => {
    const editableIds = couponUserStore.filteredCouponUsers
      .filter(() => canEdit())
      .map((cu) => cu.couponUserId);

    const allIdsOnPage = couponUserStore.filteredCouponUsers.map(
      (cu) => cu.couponUserId
    );

    if (value) {
      editableIds.forEach((id) => {
        if (!couponUserStore.selectedCouponUserIds.includes(id))
          couponUserStore.selectedCouponUserIds.push(id);
      });
    } else {
      couponUserStore.selectedCouponUserIds =
        couponUserStore.selectedCouponUserIds.filter(
          (id) => !allIdsOnPage.includes(id)
        );
    }
  },
});

const canBatchActions = computed(
  () =>
    Array.isArray(couponUserStore.selectedCouponUserIds) &&
    couponUserStore.selectedCouponUserIds.length > 0
);

// ─────────────────────────────────────────────
// AÇÕES DE LINHA
// ─────────────────────────────────────────────
function toggleRowDropdown(id) {
  openDropdownId.value = openDropdownId.value === id ? null : id;
}

function handleEdit(cu) {
  selectedCouponUser.value = cu;
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(cu) {
  handleOpenCard(cu);
  openDropdownId.value = null;
}

function handleDelete(cu) {
  selectedCouponUser.value = cu;
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(id) {
  couponUserStore.toggleSelection(id);
}

// ─────────────────────────────────────────────
// FETCH
// ─────────────────────────────────────────────
async function fetchCouponUsers() {
  try {
    isLoadingTable.value = true;

    if (!isSys.value && callerId.value) {
      couponUserStore.filters.userId = callerId.value;
    }

    await couponUserStore.fetchCouponUsersPaged(
      currentPage.value,
      pageSize.value,
      couponUserStore.userIdFilter
    );
  } catch (error) {
    console.error("❌ Erro ao buscar coupon_users:", error?.message);
    showErrorToast("Erro ao carregar vínculos de cupons.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchCouponUsers);

// ─────────────────────────────────────────────
// BATCH DELETE
// ─────────────────────────────────────────────
function tryOpenBatchDeleteModal() {
  if (
    !Array.isArray(couponUserStore.selectedCouponUserIds) ||
    couponUserStore.selectedCouponUserIds.length === 0
  ) {
    showErrorToast("Nenhum vínculo selecionado.");
    isBatchDropdownOpen.value = false;
    return;
  }
  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

// ─────────────────────────────────────────────
// HANDLER PÓS-MUDANÇA
// ─────────────────────────────────────────────
async function handleCouponUsersChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
    successMessage = null,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;

  if (clearSelection) couponUserStore.clearSelection();

  if (successMessage) showSuccessToast(successMessage);

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchCouponUsers();
    return;
  }

  await fetchCouponUsers();
}

// ─────────────────────────────────────────────
// CLICK FORA → fecha dropdowns
// ─────────────────────────────────────────────
function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);

  if (!isSys.value && callerId.value) {
    couponUserStore.filters.userId = callerId.value;
    couponUserStore.userIdFilter = Number(callerId.value);
  }

  fetchCouponUsers();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  isLoadingTable.value = false;
});
</script>

<template>
  <div class="w-full h-full">
    <div class="coupon-container">
      <!-- HEADER -->
      <div class="coupon-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Vínculos de Cupons (Targeted)</div>
          <div class="text-sm mt-1 text-gray-700 ">
            <template v-if="isSys">
              Gerencie os vínculos entre cupons TARGETED e usuários. Atribua,
              revogue ou acompanhe resgates efetivados.
            </template>
            <template v-else>
              Acompanhe os cupons atribuídos a você, status de revogação e datas
              de resgate.
            </template>

            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <!-- ACTION BAR -->
      <div class="action-bar">
        <div class="filters-row">
          <div class="filters-group w-full coupon-search-panel">
            <SearchCouponUserInput :is-sys="isSys" />
          </div>
        </div>

        <div class="actions-row">
          <template v-if="isSys">
            <button @click="showAddModal = true" class="btn btn-primary">
              <svg
                class="h-4 w-4 mr-1.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                />
              </svg>
              Atribuir Cupom
            </button>

            <div ref="batchDropdownRef" class="relative">
              <button
                @click="isBatchDropdownOpen = !isBatchDropdownOpen"
                class="btn btn-batch w-full"
                :disabled="!canBatchActions"
              >
                Ações em lote
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isBatchDropdownOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div v-if="isBatchDropdownOpen" class="dropdown-menu">
                <div class="py-1">
                  <a
                    href="#"
                    role="button"
                    @click.prevent="tryOpenBatchDeleteModal"
                    class="dropdown-item danger-item"
                  >
                    <i class="fas fa-trash-alt"></i>
                    <span>Apagar selecionados</span>
                  </a>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="coupon-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>
              <th style="width: 60px">Ações</th>
              <th style="width: 6%">ID</th>
              <th style="width: 14%">Cupom</th>
              <th style="width: 14%">Usuário</th>
              <th style="width: 10%">Situação</th>
              <th style="width: 10%">Resgate</th>
              <th style="width: 14%">Atribuído em</th>
              <th style="width: 14%">Revogado em</th>
              <th style="width: 14%">Resgatado em</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="cu in !isLoadingTable ? couponUserStore.filteredCouponUsers : []"
              :key="cu.couponUserId"
              class="clickable-row"
              @click="handleOpenCard(cu)"
            >
              <td @click.stop>
                <input
                  type="checkbox"
                  :value="cu.couponUserId"
                  :checked="
                    couponUserStore.selectedCouponUserIds?.includes(
                      cu.couponUserId
                    )
                  "
                  @change="toggleSelection(cu.couponUserId)"
                  class="checkbox-style"
                  :disabled="!canEdit()"
                  :class="{ 'disabled-item': !canEdit() }"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <div class="row-actions">
                  <button
                    @click="toggleRowDropdown(cu.couponUserId)"
                    class="btn-icon btn-soft-brand"
                    title="Menu de Ações"
                  >
                    <i class="fas fa-ellipsis-h fa-lg"></i>
                  </button>
                </div>

                <div
                  v-if="openDropdownId === cu.couponUserId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      v-if="isSys"
                      :class="{ 'disabled-item': !canEdit() }"
                      @click="canEdit() && handleEdit(cu)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>
                    <li @click="handleView(cu)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>
                    <li
                      v-if="isSys"
                      class="danger-item"
                      :class="{ 'disabled-item': !canDelete() }"
                      @click="canDelete() && handleDelete(cu)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="cu.couponUserId">{{ cu.couponUserId }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="cu.couponCode ?? cu.couponId">
                  {{ cu.couponCode ?? `#${cu.couponId}` }}
                </span>
              </td>

              <td class="cell-text-center">
                <span :title="cu.userName ?? cu.userId">
                  {{ cu.userName ?? `#${cu.userId}` }}
                </span>
              </td>

              <td>
                <span :class="getRevokedClass(cu.revokedAt)">
                  {{ getRevokedLabel(cu.revokedAt) }}
                </span>
              </td>

              <td>
                <span :class="getRedeemedClass(cu.redeemedAt)">
                  {{ getRedeemedLabel(cu.redeemedAt) }}
                </span>
              </td>

              <td>
                <span :title="formatDateTimeForDisplay(cu.assignedAt)">
                  {{ formatDateTimeForDisplay(cu.assignedAt) }}
                </span>
              </td>

              <td>
                <span
                  :title="
                    cu.revokedAt ? formatDateTimeForDisplay(cu.revokedAt) : '—'
                  "
                >
                  {{
                    cu.revokedAt ? formatDateTimeForDisplay(cu.revokedAt) : "—"
                  }}
                </span>
              </td>

              <td>
                <span
                  :title="
                    cu.redeemedAt
                      ? formatDateTimeForDisplay(cu.redeemedAt)
                      : '—'
                  "
                >
                  {{
                    cu.redeemedAt
                      ? formatDateTimeForDisplay(cu.redeemedAt)
                      : "—"
                  }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="10" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando vínculos de cupons...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="(couponUserStore.totalCount ?? 0) === 0"
              class="empty-row-state"
            >
              <td colspan="10" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhum vínculo encontrado com os filtros aplicados.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoadingTable && (couponUserStore.totalCount ?? 0) > 0"
          class="coupon-pagination-row"
        >
          <div class="coupon-pagination-left">
            <span class="coupon-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="couponUserStore.totalCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="couponUserStore.totalCount ?? 0"
            :total-pages="couponUserStore.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <CouponUserCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :coupon-user-id="selectedCardCouponUserId"
      @close="showCardModal = false"
    />
    <CouponUserHelpModal
      :show="showHelpModal"
      @close="showHelpModal = false"
    />
  </Teleport>

  <Teleport to="body">
    <AddCouponUserForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @refresh="
        handleCouponUsersChanged({ goToFirstPage: true, closeAdd: true })
      "
    />

    <EditCouponUserForm
      v-if="showEditModal"
      :coupon-user="selectedCouponUser"
      @close="showEditModal = false"
      @refresh="
        handleCouponUsersChanged({ goToFirstPage: false, closeEdit: true })
      "
    />

    <DeleteCouponUserForm
      v-if="showDeleteModal"
      :coupon-user="selectedCouponUser"
      @close="showDeleteModal = false"
      @refresh="
        handleCouponUsersChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
    />

    <BatchDeleteCouponUserForm
      v-if="showBatchDeleteModal"
      :coupon-user-ids="couponUserStore.selectedCouponUserIds"
      @close="showBatchDeleteModal = false"
      @refresh="
        handleCouponUsersChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.coupon-container {
  background-color: var(--bg-content);
  border-radius: 16px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-size: 0.95rem;
}

.coupon-header {
  background: var(--bg-header);
  padding: 0.75rem 1.5rem;
  color: var(--text-dark);
}

.filters-row {
  display: flex;
  width: 100%;
}

.actions-row {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}

@media (max-width: 767px) {
  .actions-row {
    flex-direction: column;
    align-items: stretch;
  }
}

.action-bar input,
.action-bar .search-input,
.search-text-input input {
  font-size: 0.8rem !important;
}

.filters-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@media (min-width: 768px) {
  .filters-group {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }
}

.filter-select {
  height: 32px;
  padding: 0.3rem 1.6rem 0.3rem 0.5rem;
  font-size: 0.8rem;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-width: 160px;
  box-sizing: border-box;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.45rem center;
  background-size: 18px 18px;
}
.filter-select:hover {
  border-color: var(--focus-border);
}
.filter-select:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.search-input-temp {
  width: 100%;
  height: 32px;
  padding: 0.3rem 0.5rem;
  font-size: 0.8rem;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  box-sizing: border-box;
  flex: 0 1 50%;
  min-width: 160px;
  max-width: 50%;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.search-input-temp:hover {
  border-color: var(--focus-border);
}
.search-input-temp:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.dropdown-divider {
  height: 1px;
  background-color: var(--border-color);
  margin: 6px 0;
}
.dropdown-toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.65rem;
  cursor: default;
}
.label-text {
  font-size: 0.8rem;
  color: var(--text-dark);
}
.row-dropdown-menu .toggle-switch {
  width: 38px;
  height: 20px;
}
.row-dropdown-menu .slider {
  border-radius: 999px;
}
.row-dropdown-menu .slider:before {
  width: 14px;
  height: 14px;
  left: 3px;
  bottom: 3px;
}
.row-dropdown-menu input:checked + .slider:before {
  transform: translateX(18px);
}

.coupon-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  text-align: center;
}
.coupon-table th {
  padding: 0.6rem 0.5rem;
  text-transform: uppercase;
  font-size: 0.7rem;
  color: var(--text-dark);
  background: var(--bg-header);
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 10;
}
.coupon-table tr {
  border-bottom: 1px solid var(--border-color);
}
.coupon-table tbody tr:not(.empty-row-state):hover {
  background-color: var(--bg-active-nav);
}
.coupon-table td {
  padding: 0.3rem 0.4rem;
  font-size: 0.8rem;
  vertical-align: middle;
}
.coupon-table .cell-text-left {
  text-align: left;
}
.coupon-table .cell-text-center {
  text-align: center;
}
.coupon-table td > span,
.coupon-table td > div {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 auto;
}
.coupon-table td > div {
  display: inline-block;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.coupon-code-cell {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
}

.expires-cell.expires-expired {
  color: #c92a2a;
  font-weight: 600;
}

.status-tag {
  display: inline-block;
  padding: 0.125rem 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
}
.status-red {
  background-color: #ffc9c9;
  color: #c92a2a;
}
.status-orange {
  background-color: #ffd8a8;
  color: #e8590c;
}
.status-blue {
  background-color: #a5d8ff;
  color: #1971c2;
}
.status-green {
  background-color: #b2f2bb;
  color: #2b8a3e;
}
.status-yellow {
  background-color: #fff3bf;
  color: #f59f00;
}
.status-gray {
  background-color: var(--neutral-200);
  color: #495057;
}

.discount-badge {
  background-color: #e0dbff;
  color: var(--text-on-brand-soft);
  font-weight: 600;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--neutral-200);
  transition: 0.4s;
  border-radius: 28px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: var(--surface);
  transition: 0.4s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: var(--success);
}
input:checked + .slider:before {
  transform: translateX(22px);
}
input:disabled + .slider {
  cursor: not-allowed;
  opacity: 0.5;
}

.status-toggle-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}
.status-column-toggle {
  width: 38px;
  height: 20px;
}
.status-column-toggle .slider:before {
  width: 14px;
  height: 14px;
  left: 3px;
  bottom: 3px;
}
.status-column-toggle input:checked + .slider:before {
  transform: translateX(18px);
}

.chips-note-count {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 28px;
  height: 22px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-dark);
  background-color: var(--neutral-200);
  border: 1px solid #ced4da;
  transition: all 0.2s;
}
.chips-note-count.active-notes {
  background-color: #ccf7ff;
  color: #1a4a4f;
  border-color: #9bf6ff;
}

.coupon-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.87rem;
}
.coupon-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.coupon-pagination-label {
  padding-top: 2px;
}

.table-status-toggle {
  width: 42px;
  height: 22px;
}

.table-status-toggle .slider:before {
  width: 16px;
  height: 16px;
  left: 3px;
  bottom: 3px;
}

.table-status-toggle input:checked + .slider:before {
  transform: translateX(20px);
}

.coupon-search-panel {
  width: 100%;
}

.coupon-search-panel :deep(*) {
  box-sizing: border-box;
}

.coupon-search-panel :deep(input),
.coupon-search-panel :deep(select),
.coupon-search-panel :deep(.filter-select),
.coupon-search-panel :deep(.search-input),
.coupon-search-panel :deep(.search-input-temp) {
  height: 32px;
  font-size: 0.8rem !important;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.coupon-search-panel :deep(input:hover),
.coupon-search-panel :deep(select:hover),
.coupon-search-panel :deep(.filter-select:hover),
.coupon-search-panel :deep(.search-input:hover) {
  border-color: var(--focus-border);
}

.coupon-search-panel :deep(input:focus),
.coupon-search-panel :deep(select:focus),
.coupon-search-panel :deep(.filter-select:focus),
.coupon-search-panel :deep(.search-input:focus) {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.date-cell {
  font-size: smaller;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

.coupon-table td > div.empty-state-content {
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100% !important;
  max-width: none !important;
  min-height: 180px;
  margin: 0 auto !important;
  text-align: center;
}

</style>